import type { GlobalSEOConfig, PageSEOConfig } from '../types/seo'

/**
 * Global SEO configuration for Lucas Stufflebeam Portfolio
 */
export const GLOBAL_SEO: GlobalSEOConfig = {
  siteName: 'Lucas Stufflebeam Portfolio',
  siteUrl: 'https://lucasstufflebeam.com',
  defaultImage: '/og-default.jpg',
  defaultImageAlt: 'Lucas Stufflebeam - Principal Software Architect Portfolio',
  author: 'Lucas Stufflebeam',
  twitterHandle: '@lstufflebeam',
  locale: 'en_US',
  themeColor: '#0e1528',
}

/**
 * Page-specific SEO configurations
 */
export const PAGE_SEO_CONFIG: Record<string, PageSEOConfig> = {
  home: {
    seo: {
      title: 'Lucas Stufflebeam - Principal Software Architect | Portfolio',
      description:
        'Professional portfolio of Lucas Stufflebeam, Principal Software Architect with 15+ years experience building scalable, high-performance backend systems for global applications.',
      keywords: [
        'software architect',
        'professional portfolio',
        'cloud architecture',
        'kubernetes',
        'microservices',
        'devops',
        'lucas stufflebeam',
        'principal architect',
        'scalable backends',
        'enterprise solutions',
      ],
    },
    openGraph: {
      title: 'Lucas Stufflebeam - Principal Software Architect | Portfolio',
      description:
        'Professional portfolio showcasing expertise in software architecture, cloud technologies, and scalable backend development.',
      type: 'website',
      url: 'https://lucasstufflebeam.com',
      image: '/og-home.jpg',
      imageAlt: 'Lucas Stufflebeam - Principal Software Architect Portfolio',
      siteName: 'Lucas Stufflebeam Portfolio',
      locale: 'en_US',
    },
    twitterCard: {
      card: 'summary_large_image',
      title: 'Lucas Stufflebeam - Principal Software Architect | Portfolio',
      description:
        'Professional portfolio showcasing expertise in software architecture, cloud technologies, and scalable backend development.',
      image: '/og-home.jpg',
      imageAlt: 'Lucas Stufflebeam - Principal Software Architect Portfolio',
      creator: '@lstufflebeam',
    },
    structuredData: [
      {
        '@context': 'https://schema.org',
        '@type': 'Person',
        name: 'Lucas Stufflebeam',
        description:
          'Principal Software Architect specializing in scalable backend systems, cloud architecture, and enterprise solutions',
        url: 'https://lucasstufflebeam.com',
        jobTitle: 'Principal Software Architect',
        sameAs: [
          'https://linkedin.com/in/lstufflebeam',
          'https://github.com/lstufflebeam',
        ],
        knowsAbout: [
          'Software Architecture',
          'Cloud Computing',
          'Kubernetes',
          'Microservices',
          'DevOps',
          'Backend Development',
        ],
      },
    ],
  },

  resume: {
    seo: {
      title: 'Resume - Lucas Stufflebeam | Principal Software Architect',
      description:
        "Download Lucas Stufflebeam's professional resume. Principal Software Architect with 15+ years experience in scalable backend systems, cloud-native architecture, Kubernetes, microservices, and enterprise DevOps solutions.",
      keywords: [
        'resume',
        'cv',
        'principal software architect',
        'scalable backends',
        'enterprise development',
        'cloud architecture',
        'kubernetes expert',
        'microservices',
        'devops automation',
        'lucas stufflebeam',
      ],
    },
    openGraph: {
      title: 'Resume - Lucas Stufflebeam | Principal Software Architect',
      description:
        'Professional resume: 15+ years building scalable backend systems, cloud-native architecture, and enterprise DevOps solutions.',
      type: 'profile',
      url: 'https://lucasstufflebeam.com/resume',
      image: '/og-resume.jpg',
      imageAlt:
        'Lucas Stufflebeam Professional Resume - Principal Software Architect',
      siteName: 'Lucas Stufflebeam Portfolio',
      locale: 'en_US',
    },
    twitterCard: {
      card: 'summary_large_image',
      title: 'Resume - Lucas Stufflebeam | Principal Software Architect',
      description:
        'Professional resume: 15+ years building scalable backend systems and cloud-native architecture.',
      image: '/og-resume.jpg',
      imageAlt:
        'Lucas Stufflebeam Professional Resume - Principal Software Architect',
      creator: '@lstufflebeam',
    },
  },

  about: {
    seo: {
      title:
        'About Lucas Stufflebeam | Principal Software Architect & Technical Leader',
      description:
        'Meet Lucas Stufflebeam, Principal Software Architect with 15+ years leading scalable backend development, cloud-native architecture, and enterprise DevOps transformation. Based in Denver, CO with remote availability.',
      keywords: [
        'about lucas stufflebeam',
        'principal software architect',
        'technical leadership',
        'backend development',
        'cloud native architecture',
        'enterprise devops',
        'denver colorado',
        'remote work',
        'systems thinking',
        'scalable infrastructure',
      ],
    },
    openGraph: {
      title: 'About Lucas Stufflebeam | Principal Software Architect',
      description:
        'Principal Software Architect with 15+ years leading scalable backend development and cloud-native architecture. Denver, CO • Remote Available',
      type: 'profile',
      url: 'https://lucasstufflebeam.com/about',
      image: '/og-about.jpg',
      imageAlt:
        'Lucas Stufflebeam - Principal Software Architect professional headshot',
      siteName: 'Lucas Stufflebeam Portfolio',
      locale: 'en_US',
    },
    twitterCard: {
      card: 'summary_large_image',
      title: 'About Lucas Stufflebeam | Principal Software Architect',
      description:
        'Principal Software Architect: 15+ years scalable backend systems & cloud-native architecture. Denver, CO • Remote Available',
      image: '/og-about.jpg',
      imageAlt:
        'Lucas Stufflebeam - Principal Software Architect professional headshot',
      creator: '@lstufflebeam',
    },
    structuredData: [
      {
        '@context': 'https://schema.org',
        '@type': 'Person',
        name: 'Lucas Stufflebeam',
        jobTitle: 'Principal Software Architect',
        description:
          'Principal Software Architect with 15+ years of experience in scalable backend systems, cloud-native architecture, enterprise DevOps, and Kubernetes.',
        url: 'https://lucasstufflebeam.com',
        image: 'https://lucasstufflebeam.com/og-about.jpg',
        sameAs: [
          'https://linkedin.com/in/lstufflebeam',
          'https://github.com/lstufflebeam',
        ],
        workLocation: {
          '@type': 'Place',
          name: 'Denver, CO',
          description: 'Remote work available',
        },
        knowsAbout: [
          'Software Architecture',
          'Microservices',
          'Kubernetes',
          'Docker',
          'Cloud Computing',
          'AI/ML Integration',
          'DevOps',
          'Enterprise Development',
          'Real-time Systems',
          'Distributed Systems',
        ],
        email: 'lucas@lucasstufflebeam.com',
        worksFor: {
          '@type': 'Organization',
          name: 'Independent Consultant',
        },
        alumniOf: {
          '@type': 'CollegeOrUniversity',
          name: 'University of Colorado Boulder',
        },
      },
    ],
  },

  projects: {
    seo: {
      title:
        'Portfolio Projects - Lucas Stufflebeam | Software Architecture & Development',
      description:
        "Explore Lucas Stufflebeam's portfolio of software architecture projects: enterprise platforms serving 100,000+ users, cloud-native DevOps solutions, scalable backend systems, and cloud infrastructure with measurable business impact.",
      keywords: [
        'portfolio projects',
        'software architecture',
        'enterprise platforms',
        'backend systems',
        'cloud infrastructure',
        'enterprise devops',
        'microservices',
        'scalable systems',
        'lucas stufflebeam projects',
        'technical case studies',
      ],
    },
    openGraph: {
      title: 'Portfolio Projects - Lucas Stufflebeam | Software Architecture',
      description:
        'Explore enterprise platforms, scalable backend systems, and cloud-native infrastructure projects with measurable business impact.',
      type: 'website',
      url: 'https://lucasstufflebeam.com/projects',
      image: '/og-projects.jpg',
      imageAlt:
        'Lucas Stufflebeam Portfolio Projects - Software Architecture Case Studies',
      siteName: 'Lucas Stufflebeam Portfolio',
      locale: 'en_US',
    },
    twitterCard: {
      card: 'summary_large_image',
      title: 'Portfolio Projects - Lucas Stufflebeam | Software Architecture',
      description:
        'Enterprise platforms, scalable backend systems, and cloud-native infrastructure with measurable business impact.',
      image: '/og-projects.jpg',
      imageAlt:
        'Lucas Stufflebeam Portfolio Projects - Software Architecture Case Studies',
      creator: '@lstufflebeam',
    },
    structuredData: [
      {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: 'Portfolio Projects',
        description:
          'Comprehensive portfolio of software architecture and development projects',
        url: 'https://lucasstufflebeam.com/projects',
        author: {
          '@type': 'Person',
          name: 'Lucas Stufflebeam',
          jobTitle: 'Principal Software Architect',
        },
        mainEntity: {
          '@type': 'ItemList',
          name: 'Technical Projects',
          description:
            'Enterprise systems, scalable backends, and cloud infrastructure',
        },
      },
    ],
  },
}

/**
 * Helper function to get SEO config for a specific page
 */
export const getPageSEO = (page: string): PageSEOConfig | null => {
  return PAGE_SEO_CONFIG[page] || null
}

/**
 * Helper function to generate full URL for canonical links
 */
export const getCanonicalUrl = (path: string): string => {
  return `${GLOBAL_SEO.siteUrl}${path === '/' ? '' : path}`
}
